import { useId, useRef, useState, type FormEvent } from 'react';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { UpgradeRefusalNotice } from '../../../shared/UpgradeRefusalNotice';
import { clearSubscriptionRestrictionNotice, upgradeRequiredFromError, type SubscriptionRestriction } from '../../../shared/subscriptionRestriction';
import { ExportValidationError, exportErrorMessage } from '../exportErrors';
import type { ExportDetail, ExportMessage, ExportSummary } from '../exportTypes';
import { accountingGateway } from '../index';

const REASON_MAX = 500;

/**
 * The reason dialog for a reversal or a replacement. A reversal artifact is a NEW export whose lines are the original's with debit and credit swapped;
 * a replacement is a new export built from the corrected payroll. Neither posts anything anywhere: the files are for the person to apply in their own
 * accounting software. The reason is required and is recorded with who asked.
 */
export function ReasonDialog({ kind, target, onClose, onDone }: { kind: 'reversal' | 'replacement'; target: ExportSummary; onClose: () => void; onDone: (created: ExportDetail) => void }) {
  const uid = useId();
  const summaryRef = useRef<HTMLDivElement>(null);
  const [reason, setReason] = useState('');
  const [issues, setIssues] = useState<SummaryIssue[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<ExportMessage[]>([]);
  const [busy, setBusy] = useState(false);
  // A replacement needs the accounting export in the plan (a reversal never does): the refusal is explained here, with the reason kept.
  const [planRefusal, setPlanRefusal] = useState<SubscriptionRestriction | null>(null);
  const reversal = kind === 'reversal';
  const reasonId = `${uid}-reason`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    setBlockers([]);
    setPlanRefusal(null);
    const found: SummaryIssue[] = [];
    if (!reason.trim()) found.push({ key: 'reason', message: reversal ? 'Enter the reason for this reversal.' : 'Enter the reason for this replacement.' });
    if (reason.trim().length > REASON_MAX) found.push({ key: 'reason', message: `Keep the reason to ${REASON_MAX} characters or fewer.` });
    setIssues(found);
    if (found.length > 0) {
      queueMicrotask(() => summaryRef.current?.focus());
      return;
    }
    setBusy(true);
    try {
      const created = reversal ? await accountingGateway.reverseExport(target.id, reason.trim()) : await accountingGateway.replaceExport(target.id, { reason: reason.trim() });
      onDone(created);
    } catch (err) {
      const refusal = upgradeRequiredFromError(err);
      if (refusal) {
        clearSubscriptionRestrictionNotice();
        setPlanRefusal(refusal);
      } else if (err instanceof ExportValidationError) {
        setBlockers(err.report.blockers);
        setServerError('The corrected payroll is blocked by validation, so no replacement was created.');
      } else {
        setServerError(exportErrorMessage(err, reversal ? 'The reversal could not be generated.' : 'The replacement could not be generated.'));
      }
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title={reversal ? 'Generate reversal artifact' : 'Generate replacement export'}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={`${uid}-form`} className="jr-btn jr-btn--primary" disabled={busy}>
            {busy ? 'Generating…' : reversal ? 'Generate reversal artifact' : 'Generate replacement'}
          </button>
        </>
      )}
    >
      <form id={`${uid}-form`} onSubmit={(e) => void submit(e)} noValidate>
        <p>
          {reversal
            ? <>This creates a new export for <strong>{target.journalReference}</strong> with every debit and credit swapped, so the original entry nets to zero. </>
            : <>This builds a new export for <strong>{target.journalReference}</strong> from the payroll as it stands now, after the correction. </>}
          <strong>Nothing is posted to any accounting system.</strong> You download the file and apply it yourself.
        </p>
        <p className="jr-hint">
          {reversal
            ? 'The original export is kept unchanged and marked Reversed. One reversal is allowed per export.'
            : 'The original export must already be reversed, and the payroll must have changed since it was exported. The original is then marked Superseded.'}
        </p>
        <ErrorSummary ref={summaryRef} issues={issues} onSelect={() => document.getElementById(reasonId)?.focus()} />
        {serverError && <p className="jr-error jr-error--block" role="alert">{serverError}</p>}
        {planRefusal && <UpgradeRefusalNotice restriction={planRefusal} fallbackFeatureName="Accounting export (CSV)" nothingChanged="No replacement was created. Your reason is kept." />}
        {blockers.length > 0 && (
          <ul className="jr-plain-list" data-testid="replacement-blockers">
            {blockers.map((b, i) => <li key={`${b.code}-${i}`}>{b.message}</li>)}
          </ul>
        )}
        <div className="jr-field">
          <label htmlFor={reasonId}>Reason <span aria-hidden="true" className="jr-req">*</span></label>
          <textarea
            id={reasonId} className="ax-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={REASON_MAX} required data-autofocus
            aria-describedby={`${reasonId}-hint`} {...(issues.length > 0 ? { 'aria-invalid': true } : {})}
          />
          <p id={`${reasonId}-hint`} className="jr-hint ax-counter">{reason.length} of {REASON_MAX} characters. The reason is recorded with your name in the audit history.</p>
          {issues.map((i) => <p key={i.message} className="jr-error">{i.message}</p>)}
        </div>
      </form>
    </ModalDialog>
  );
}
